/**
 * Google Places API utilities for enhanced postcode/suburb search
 * with radius-based location finding
 */

export interface PlaceResult {
  place_id: string
  formatted_address: string
  name: string
  geometry: {
    location: {
      lat: number
      lng: number
    }
  }
  types: string[]
  address_components: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
}

export interface PostcodeInfo {
  postcode: string
  suburb: string
  state: string
  country: string
  lat: number
  lng: number
  formatted_address: string
}

export interface NearbyPostcode {
  postcode: string
  suburb: string
  distance: number // km from search center
  lat: number
  lng: number
}

/**
 * Search for places (postcodes/suburbs) using Google Places API
 */
export async function searchPlaces(query: string): Promise<PlaceResult[]> {
  // Client-side validation: minimum 3 chars for suburb, 4 for postcode
  const isPostcode = /^\d+$/.test(query)
  const minLength = isPostcode ? 4 : 3

  if (!query || query.length < minLength) {
    return []
  }

  try {
    // Focus search on Australia and New Zealand
    const countryRestriction = process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz' ? 'nz' : 'au'

    // New API format - simplified parameters
    const response = await fetch(
      `/api/places/autocomplete?` +
        `input=${encodeURIComponent(query)}` +
        `&country=${countryRestriction}`
    )

    if (!response.ok) {
      if (response.status === 429) {
        console.warn('Rate limit exceeded')
      }
      return []
    }

    const data = await response.json()

    if (data.error) {
      console.error('Places API Error:', data.error)
      return []
    }

    return data.predictions ?? []
  } catch (error) {
    console.error('Error searching places:', error)
    return []
  }
}

/**
 * Get detailed place information including coordinates
 */
export async function getPlaceDetails(placeId: string): Promise<PostcodeInfo | null> {
  try {
    // New API format - no fields parameter needed
    const response = await fetch(`/api/places/details?place_id=${encodeURIComponent(placeId)}`)

    if (!response.ok) {
      if (response.status === 429) {
        console.warn('Rate limit exceeded')
      }
      return null
    }

    const data = await response.json()

    if (data.error) {
      console.error('Place Details API Error:', data.error)
      return null
    }

    // API route already returns the data in the correct format
    return data
  } catch (error) {
    console.error('Error getting place details:', error)
    return null
  }
}

/**
 * @deprecated This function makes 50+ API calls per search and is extremely expensive.
 * Office filtering is now done client-side using the cached office database.
 * DO NOT USE this function - it will result in high API costs.
 */
export async function getNearbyPostcodes(
  _centerLat: number,
  _centerLng: number,
  _radiusKm: number = 20
): Promise<NearbyPostcode[]> {
  // Suppress unused variable warnings - parameters kept for API compatibility
  void _centerLat
  void _centerLng
  void _radiusKm

  console.warn(
    'getNearbyPostcodes is deprecated and should not be called. ' +
      'This function makes 50+ API calls and is very expensive. ' +
      'Use office filtering with batched geocoding instead.'
  )
  return []
}

/**
 * Calculate distance between two points using Haversine formula
 * @deprecated Use calculateDistance from components instead
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371 // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * Radius options for the dropdown
 */
export const RADIUS_OPTIONS = [
  { value: 20, label: '20km' },
  { value: 30, label: '30km' },
  { value: 50, label: '50km' },
  { value: 100, label: '100km' },
] as const

export type RadiusValue = (typeof RADIUS_OPTIONS)[number]['value']
